/**
 * @ngdoc services
 * @memberof mercury.services
 * @name userinfo
 * @description Service to manage the user information
 */
angular.module('mercury.services.userinfo')
.factory('userinfo', ["dialog", "$http", "localStorageService", "$state", "$q", "reporttime", "$rootScope", "$window", "settings", "features", "applicationMenu", "logger", "helperUtils", function userinfoFactory(
  dialog, $http, localStorageService, $state, $q, reporttime, $rootScope,
  $window, settings, features, applicationMenu, logger, helperUtils
) {
  var _activeCompanyId = null;
  var _companies = null;
  var _userDetails = {};
  var _readyQueue = [];

  var pendingUserInfoRequest = null;

  var pendingInviteRequest = null;

  // List of known states and company speicific information for them.
  var stateInformation = {};

  /**
   * Return the parsed state information for the current company.
   * @returns
   */
  function getStateInformation() {
    return stateInformation;
  }

  // Recursively traverse the companyReports structure and flattend it into an object
  // keyed with the state name, and with extra information about displaying that state.
  function parseStateInformationFromMenuItems(menuItems) {
    menuItems.forEach(function(item) {
      if (item.state) {
        stateInformation[item.state] = {
          showPlaceholder: item.showPlaceholder,
          reportTimeTypes: item.reportTimeTypes
        };
      }

      if (item.subMenu && item.subMenu.length > 0) {
        parseStateInformationFromMenuItems(item.subMenu);
      }
    });
  }

  function parseStateInformationFromCompanyReports() {
    var deferred = $q.defer();
    applicationMenu.getMenu()
      .then(function(reports) {
        reports.forEach(function(item) {
          parseStateInformationFromMenuItems(item.menuItems);
          deferred.resolve();
        });
      }, function() {
        logger.warn('unable to get application menu for currently active company');
      });
    return deferred.promise;
  }

  // When the current window is focused, store the activeCompany in local storage so if the user opens a
  // new windows, we can default to this company instead of the first company.
  $(window).focus(function() {
    if (_activeCompanyId) {
      localStorageService.set('defaultCompany', _activeCompanyId);
    }
  });

  /**
   * Get the preference value of a given key
   *
   * @memberof userinfo
   * @param {String} key Key value of the preference to return
   * @param {String} companyId Specify the company to get the preference of. Default activeCompany
   */
  function companyPreferenceGet(key, companyId) {
    if (!_companies) {
      return null;
    } else if (companyId) {
      return angular.copy(_companies[companyId].preferences[key]) || null;
    } else {
      return angular.copy(_companies[_activeCompanyId].preferences[key]) || null;
    }
  }

  /**
   * Get or set the active company id
   *
   * @memberof userinfo
   * @param {String} companyId If set, update the activeCompany to this value.
   */
  function activeCompany(companyId) {
    if (angular.isDefined(companyId)) {
      _activeCompanyId = companyId;

      // Update the active company in menu/features services - it's very
      // important this is done before we ever try and call methods from
      // the same services otherwise assumptions in the services about
      // what the currently active company is can be incorrect.
      applicationMenu.setActiveCompany(companyId);
      features.setActiveCompany(companyId);

      // Update the stored default company to be used in new windows.
      localStorageService.set('defaultCompany', companyId);

      moment.locale(companyPreferenceGet('locale.locale'));
      moment.tz.setDefault(companyPreferenceGet('locale.timeZone'));

      // Set the x-loaded-company-id default header so all requests are associated with this company.
      $http.defaults.headers.common['x-loaded-company-id'] = companyId;

      //Store the activeCompanyId in sessionStorage so it can be set upon page reload.
      sessionStorage.activeCompany = companyId;

      // Update the day and week starts stored in reporttime service
      reporttime.dayStart = companyPreferenceGet('date.daycutoff.daystart');
      reporttime.weekStart = companyPreferenceGet('date.daycutoff.weekstart');

      parseStateInformationFromCompanyReports().then(function() {
        reporttime.setCompanyStateInformation(stateInformation);
      });

      // add company details to user properties in Google Analytics
      /* jshint ignore:start */
      gtag('set', 'user_properties', {
        'company_id': _activeCompanyId,
        'company_name': companyName()
      });

      // We need to make this call only when the window.clarity object
      // exists - it gets injected via Google Tag Manager and therefore
      // is not always reliably available at the point we are evaluating
      // this code on page load.
      helperUtils.waitFor('clarity', () => {
        clarity("identify", _userDetails.id, null, null, _userDetails.email);
        clarity("set", {
          'company_id': _activeCompanyId,
          'company_name': companyName()
        });
      })

      /* jshint ignore:end */
    }
    return _activeCompanyId;
  }

  /**
   * Get the name of a company.
   *
   * @memberof userinfo
   * @param {String} companyId Specify the company to get the name of. Default activeCompany
   */
  function companyName(companyId) {
    if (!companyId) {
      companyId = activeCompany();
    }
    return _companies[companyId].name;
  }

  /**
   * The Subscription Tier for this company.
   * This is in the form "Tier1", "Tier2" etc, rather than using the marketing names.
   *
   * @memberof userinfo
   * @param {String} companyId Specify the company to get the tier of. Default activeCompany
   */
  function companyTier(companyId) {
    //This is really similar to the above function. If we need to keep adding these it might
    //be a good idea to just return the company object and let the caller decide what fields to use
    if (!companyId) {
      companyId = activeCompany();
    }
    return _companies[companyId].subscriptionTier;
  }

  /**
   * Return the number of companies the user has access to
   *
   * @memberof userinfo
   */
  function countCompanies() {
    return Object.keys(_companies).length;
  }

  /**
   * Return a promise that will resolve once userinfo has been established. The user
   * info will be provided as an argument to the promise chain.
   */
  function ready() {
    return $q(function(resolve) {
      if (_companies !== null) {
        resolve(_userDetails);
      }

      // if not yet initialised just queue up for resolution
      // once the main initialize method has been called by
      // someone else.
      _readyQueue.push(resolve);

    });
  }

  /**
   * Fetch all the user information from the Server
   *
   * @memberof userinfo
   */
  function initialize() {
    var deferred = $q.defer();
    //Only allow a single UserInformation request at a time. All additional requests
    //should return the promise generated from the first request.
    if (pendingUserInfoRequest === null) {
      pendingUserInfoRequest = deferred.promise;

      $http.get(settings.mercHost + '/user-session')
      .success(function(userInfo) {

        _userDetails.name = userInfo.firstName + ' ' + userInfo.lastName;
        _userDetails.firstName = userInfo.firstName;
        _userDetails.lastName = userInfo.lastName;
        _userDetails.email = userInfo.userName;
        _userDetails.hasPassword = userInfo.hasPassword;
        _userDetails.id = userInfo.id;
        _userDetails.logins = userInfo.logins;
        _userDetails.isLegacyUser = userInfo.isLegacyUser;
        _userDetails.intercomUserHash = userInfo.intercomUserHash;

        _companies = {};
        angular.forEach(userInfo.companies, function(company) {
          _companies[company.id] = company;
        });

        // Start by checking sessionStorage for the activeCompany. This is limited
        // to this tab/window and will be set if the page was refreshed.
        var activeCompanyId = sessionStorage.activeCompany;

        // Next check the localStorage which will be set if another tab has logged in
        // before.
        if (!activeCompanyId || !_companies[activeCompanyId]) {
          activeCompanyId = localStorageService.get('defaultCompany');
        }

        // Finally default to the first company.
        if (!activeCompanyId || !_companies[activeCompanyId]) {
          activeCompanyId = null;

          if (countCompanies() > 0) {
            activeCompanyId = userInfo.companies[0].id;
          }
        }

        if (activeCompanyId) {

          activeCompany(activeCompanyId);

          applicationMenu.getMenu().then(function() {
            // we only release the promise here (used for overall app loading)
            // when the application menu data for the active company has finished
            // loading - the menu data is required for rendering the page
            deferred.resolve();
          });
        }

        // Release any promises queued through calls to `ready` before now
        _readyQueue.splice(0).forEach(function(queuedResolve) {
          queuedResolve(_userDetails);
        });
        pendingUserInfoRequest = null;

        if (!activeCompanyId) {
          // If we don't have an active company id (i.e. this is a new user
          // signup) then we need to do a resolve right at the end of this
          // block to remove the app loading state. We need the user details
          // etc to be copied across prior to resolving.
          deferred.resolve();
        }
      })
      .error(function() {
        deferred.reject();
        pendingUserInfoRequest = null;
      });
    } else {
      return pendingUserInfoRequest;
    }

    return deferred.promise;
  }

  /**
   * Given a company invite code, attempt to accept the invite.
   *
   * @memberof userinfo
   * @param {String} inviteCode Invite code
   * @param {String} inviteComanyName The name of the company the invite is for.
   */
  function acceptCompanyInvite(inviteCode, inviteCompanyName) {
    var deferred = $q.defer();

    // ensure that only one invite request is sent, if there's already one pending, then return the promise for that one
    if (pendingInviteRequest === null) {
      pendingInviteRequest = deferred;

      $http.post(settings.apiHost + '/r/UserCompanyPermissions?inviteCode=' + inviteCode)
      .then(function(inviteResponse) {
        initialize()
        .then(function() {
          activeCompany(inviteResponse.data.companyId);

          deferred.resolve();

          dialog.alert('Invitation accepted. You now have access to ' + companyName());
        }, function() {
          deferred.reject();
        });
      }, function(response) {
        if (response.status == 400 || response.status == 404) {
          if (response.status == 400) {
            dialog.alert('Unable to accept this invite because you already have access to the company. ' +
              'Click "Log Out" to accept this invite with a different username.', {
              'Log Out': function() {
                localStorageService.remove('authorizationData');
                $window.location.assign('/login/InvitedUser?inviteToken=' + inviteCode + '&companyName=' + inviteCompanyName);
              }
            });
          } else {
            dialog.alert('This invite is not valid. It may have expired or the user who sent it may have revoked it.');
          }

          //Chain the response promise with the userinfo initialize
          deferred.resolve(initialize());
        } else {
          deferred.reject(response);
        }
      });
    } else {
      return pendingInviteRequest;
    }

    return deferred.promise;
  }

  /**
   * Check if initialize() has been called.
   *
   * @memberof userinfo
   */
  function isInitialized() {
    return _companies !== null;
  }

  /**
   * Remove a company from the users list of companies.
   *
   * @memberof userinfo
   * @param {String} id Id of the company to remove.
   */
  function removeCompany(id) {
    delete _companies[id];

    if (id == activeCompany()) {
      var firstCompany = Object.keys(_companies)[0];
      activeCompany(firstCompany ? firstCompany : null);
    }
    $rootScope.$broadcast('userInfoUpdated');
  }

  /**
   * Get all companies this user has access to
   *
   * @memberof userinfo
   */
  function avaliableCompanies() {
    return _companies;
  }

  function companies() {
    var companiesArray = [];
    angular.forEach(_companies, function(company) {
      companiesArray.push(company);
    });
    return companiesArray;
  }

  /**
   * Get the name of the a companies Point of Sale system
   *
   * @memberof userinfo
   * @param {String} companyId Specify the company to get the POS of. Default activeCompany
   *
   */
  function companyPos(companyId) {
    companyId = companyId || _activeCompanyId;

    return (companyId) ? _companies[companyId].pos : null;
  }

  /**
   * Get the active companies default page
   *
   * @memberof userinfo
   */
  function companyDefaultReport() {
    var deferred = $q.defer();

    var defaultPage = 'Settings.CompanyList';
    if (_activeCompanyId) {
      applicationMenu.getMenu().then(function(menus) {
        // It's possible to get an empty menus array if the user doesn't have
        // any permissions, or they  only have reports permission and the site
        // doesn't have a POS db linked.
        if (menus.length > 0) {
          deferred.resolve(menus[0].menuItems[0].state || menus[0].menuItems[0].subMenu[0].state);
        } else {
          deferred.resolve(defaultPage);
        }
      });
    } else {
      deferred.resolve(defaultPage);
    }
    return deferred.promise;
  }

  /**
   * Check if the user has the specified permissions for the specified companyId
   *
   * @memberof userinfo
   * @param {String} permissionsToCheck CSV list of permissions to check
   * @param {String} companyId Id of the company to check. Default activeCompany
   */
  function companyHasPermission(permissionsToCheck, companyId) {
    var id = companyId || _activeCompanyId;
    if (!_companies[id]) {
      return false;
    }

    var result = true;
    angular.forEach(permissionsToCheck.split(','), function(permissionToCheck) {
      result = result && _companies[id].permissions.split(',').indexOf(permissionToCheck) !== -1;
    });
    return result;
  }

  /**
   * Set a companies preference
   *
   * @memberof userinfo
   * @param {Object} preference Preference model to update
   * @param {String} companyId Id of the company to update: Default activeCompany.
   */
  function companyPreferenceSet(preference, companyId) {
    if (!companyId || !_companies[_activeCompanyId]) {
      _companies[_activeCompanyId].preferences[preference.key] = preference.value;
    } else {
      _companies[companyId].preferences[preference.key] = preference.value;
    }
    $rootScope.$broadcast('userInfoUpdated');
  }

  /**
   * Get the user details.
   *
   * @memberof userinfo
   */
  function userDetails() {
    return _userDetails;
  }

  /**
   * Update user details
   *
   * @memberof userinfo
   */
  function setUserDetails(newDetails) {
    angular.merge(_userDetails, newDetails);
    $rootScope.$broadcast('userInfoUpdated');
  }

  return {
    initialize: initialize,
    acceptCompanyInvite: acceptCompanyInvite,
    isInitialized: isInitialized,
    activeCompany: activeCompany,
    removeCompany: removeCompany,
    avaliableCompanies: avaliableCompanies,
    companies: companies,
    countCompanies: countCompanies,
    companyName: companyName,
    companyTier: companyTier,
    companyPos: companyPos,
    companyDefaultReport: companyDefaultReport,
    companyHasPermission: companyHasPermission,
    companyPreferenceGet: companyPreferenceGet,
    companyPreferenceSet: companyPreferenceSet,
    userDetails: userDetails,
    setUserDetails: setUserDetails,
    getStateInformation: getStateInformation,
    ready: ready
  };
}]);
